import React from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import { useSelector, useDispatch } from "react-redux";
import { updateWorkOrderApi } from "../../../services/api/workOrder";
import { showToast } from "../../../store/toast/toastSlice";
import { useMutation } from "@tanstack/react-query";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import ContactsForm from "../../WorkOrder/Forms/ContactsForm";
import { useSectionChanges } from "../../../hooks/useSectionChanges";

const hasText = (value) => !!String(value ?? "").trim();

const isCompleteContact = (contact) =>
  hasText(contact?.name) && hasText(contact?.position) && hasText(contact?.phoneNumber);

const hasSaveableContacts = (contacts) => (contacts ?? []).every(isCompleteContact);

const EditContactsModal = ({
  open,
  close,
  workOrderId,
  contacts: contactsData,
  workOrderManager,
}) => {
  const dispatch = useDispatch();
  const { updateWorkOrder } = useUpdateWorkOrder();

  const workOrderContacts = useSelector((state) => state.workOrder.scheduling.contacts);
  const { hasChanges, onHydrate } = useSectionChanges(workOrderContacts);
  const canSave = hasSaveableContacts(workOrderContacts?.contacts);

  const { mutateAsync: updateWorkOrderApiMutation, isPending } = useMutation({
    mutationFn: updateWorkOrderApi,
  });

  const handleSubmit = async () => {
    try {
      const payload = {
        id: workOrderId,
        contacts: workOrderContacts.contacts,
      };

      if (workOrderContacts?.workOrderManagerId) {
        payload.workOrderManagerId = workOrderContacts.workOrderManagerId;
      }

      await updateWorkOrderApiMutation(payload);

      dispatch(showToast({ message: "Contacts updated successfully!", type: "success" }));

      updateWorkOrder();
      close(false);
    } catch (error) {
      dispatch(showToast({ message: "Failed to update contacts.", type: "error" }));
    }
  };

  return (
    <BaseModal className="sm:-top-[200px] overflow-auto" open={open} close={close}>
      <div className="py-3 sm:py-7 px-4 sm:px-12 w-full sm:w-[800px] lg:w-[1000px] xl:w-[1400px]">
        <h6 className="text-dark font-normal border-b pb-1">Edit Contacts</h6>

        <div className="mb-2 mt-3">
          <ContactsForm
            open={open}
            isEditMode={true}
            workOrderData={{ contacts: contactsData, workOrderManager: workOrderManager }}
            onHydrate={onHydrate}
          />
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          onClick={handleSubmit}
          disabled={!hasChanges || !canSave || isPending}
        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default EditContactsModal;
